import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

// type: "anchor" = scrolls to an id on home page; "route" = navigates to a path.
const LINKS = [
  { kind: "route",  path: "/listings",      label: "Listings" },
  { kind: "anchor", id: "insights",         label: "Market" },
  { kind: "anchor", id: "journal",          label: "Journal" },
  { kind: "route",  path: "/about",         label: "About" },
  { kind: "route",  path: "/vision",        label: "Vision" },
  { kind: "anchor", id: "contact",          label: "Contact" },
];

export default function Navbar({ solid = false }) {
  const [scrolled, setScrolled] = useState(solid);
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (solid) return;
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [solid]);

  const handle = (link) => {
    setOpen(false);
    if (link.kind === "route") {
      nav(link.path);
      return;
    }
    // Anchor — if not on home, route home with hash; else scroll
    if (loc.pathname !== "/") {
      nav(`/#${link.id}`);
      // After nav, scroll
      setTimeout(() => {
        document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }
    document.getElementById(link.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const overVideo = !scrolled;
  const linkColor = overVideo ? "text-white/85 hover:text-[#c5a059]" : "text-[#1c1c1c]/85 hover:text-[#c5a059]";
  const logoTone = overVideo ? "light" : "dark";

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#f5f0e8]/92 backdrop-blur-xl border-b border-[#1c1c1c]/10 py-3"
          : "bg-gradient-to-b from-black/60 to-transparent py-5"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="group">
          <Logo size="md" tone={logoTone} />
        </Link>

        <nav className="hidden md:flex items-center gap-7 lg:gap-9">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => handle(l)}
              className={`text-[11px] uppercase tracking-[0.3em] link-underline transition-colors ${linkColor}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <button
          data-testid="nav-cta-private-tour"
          onClick={() => handle({ kind: "anchor", id: "contact" })}
          className={`hidden lg:inline-flex items-center gap-3 px-5 py-2.5 border uppercase text-[11px] tracking-[0.3em] transition-all duration-500 ${
            overVideo
              ? "border-[#c5a059]/70 text-[#c5a059] hover:bg-[#c5a059] hover:text-[#1c1c1c]"
              : "border-[#c5a059] text-[#1c1c1c] hover:bg-[#c5a059] hover:text-[#1c1c1c]"
          }`}
        >
          Private Tour
        </button>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className={overVideo ? "md:hidden text-white" : "md:hidden text-[#1c1c1c]"}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div data-testid="nav-mobile-panel" className="md:hidden bg-[#f5f0e8] border-t border-[#1c1c1c]/10">
          <div className="flex flex-col px-6 py-8 gap-6">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                onClick={() => handle(l)}
                className="text-left font-display text-2xl text-[#1c1c1c] hover:text-[#c5a059]"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
